/**
 * JukeboxDrawer — "Your Own Tavern" Phase B2. The player-facing picker for
 * the lobby jukebox: now-playing + live progress, the FIFO queue, and a
 * MY RECORDS grid (owned records tappable → request; locked records show
 * an honest unlock hint, same convention as the tavern decor picker).
 *
 * Pure display + `onRequest` — all queue/ownership RULES live server-side
 * (ws-router) and in the pure core (@tt/lobby/records); this component just
 * reflects `jukebox` state and never guesses at validity beyond what's
 * needed for a helpful disabled state (queue full, already-queued swap).
 */
import { useEffect, useState } from 'react';
import { Lock, Music } from 'lucide-react';
import { getCosmetic } from '@tt/lobby/cosmetics-catalog';
import { unlockHintText } from '@tt/lobby/cosmetic-source-text';
import { RECORDS, JUKEBOX_CLIP_MS, JUKEBOX_QUEUE_MAX, type JukeboxState } from '@tt/lobby/records';

interface Props {
  jukebox: JukeboxState | null;
  myCharId: string;
  ownedIds: Set<string>;
  onRequest: (recordId: string) => void;
  onClose: () => void;
}

export function JukeboxDrawer({ jukebox, myCharId, ownedIds, onRequest, onClose }: Props) {
  const playing = jukebox?.playing;
  const queue = jukebox?.queue ?? [];
  const myQueuedEntry = queue.find((e) => e.charId === myCharId);
  const queueFull = queue.length >= JUKEBOX_QUEUE_MAX && !myQueuedEntry;

  // Live 12s progress bar for the now-playing pick — ticks while the drawer
  // is open; harmless to keep running even after the clip ends (clamped).
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!playing) return;
    const t = window.setInterval(() => setTick((n) => n + 1), 200);
    return () => window.clearInterval(t);
  }, [playing?.charId, playing?.recordId, playing?.startedAt]);
  const progressPct = playing
    ? Math.max(0, Math.min(100, ((Date.now() - playing.startedAt) / JUKEBOX_CLIP_MS) * 100))
    : 0;

  return (
    <div className="settings-backdrop" role="dialog" aria-modal="true" aria-label="Jukebox" onClick={onClose} style={{ zIndex: 40 }}>
      <div className="settings-sheet jukebox-drawer" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '32rem' }}>
        <header className="settings-header">
          <h2 className="display"><Music size={18} aria-hidden="true" /> Jukebox</h2>
          <button type="button" className="link" onClick={onClose}>close</button>
        </header>

        <section className="jukebox-drawer-now">
          {playing ? (
            <>
              <div className="jukebox-drawer-now-line">
                <span aria-hidden="true">♪</span>
                <strong>{playing.name}'s pick</strong>
                <span className="muted">— {recordLabel(playing.recordId)}</span>
              </div>
              <div className="jukebox-drawer-progress">
                <div className="jukebox-drawer-progress-fill" style={{ width: `${progressPct}%` }} />
              </div>
            </>
          ) : (
            <p className="muted" style={{ margin: 0, fontSize: 13 }}>Nothing playing — tap a record below to start the music.</p>
          )}
        </section>

        {queue.length > 0 && (
          <section className="jukebox-drawer-queue">
            <h3 style={{ fontSize: 12.5, margin: '0 0 6px', color: '#e8d9b8' }}>Up next</h3>
            <div className="jukebox-drawer-queue-list">
              {queue.map((e, i) => (
                <div key={`${e.charId}-${e.at}`} className="jukebox-drawer-queue-row">
                  <span className="jukebox-drawer-queue-pos">{i + 1}</span>
                  <span>{e.name}</span>
                  <span className="muted">— {recordLabel(e.recordId)}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <h3 style={{ fontSize: 12.5, margin: '10px 0 6px', color: '#e8d9b8' }}>My records</h3>
        {queueFull && (
          <p className="muted" style={{ fontSize: 11.5, margin: '0 0 8px' }}>Queue full — wait for a slot to open up.</p>
        )}
        <div className="locker-v2-grid">
          {RECORDS.map((r) => {
            const owned = ownedIds.has(r.id);
            const isMine = myQueuedEntry?.recordId === r.id;
            const cat = getCosmetic(`record-${r.id}`);
            const hint = cat ? unlockHintText(cat.source) : 'Locked';
            const disabled = !owned || (queueFull && !isMine);
            return (
              <button
                key={r.id}
                type="button"
                className={`locker-v2-tile tt-card jukebox-record-tile${isMine ? ' selected tt-selected' : ''}${owned ? '' : ' locked'} tt-rarity-${r.rarity}`}
                onClick={() => onRequest(r.id)}
                disabled={disabled}
                title={owned ? (isMine ? `${r.label} — queued` : r.label) : `${r.label} — ${hint}`}
              >
                <span className="locker-v2-tile-preview jukebox-record-glyph" aria-hidden="true">♪</span>
                <span className="locker-v2-tile-label">{r.label}</span>
                {owned ? (
                  isMine
                    ? <small className="muted" style={{ fontSize: 10.5 }}>queued ✓ — tap another to replace</small>
                    : null
                ) : (
                  <>
                    <span className="locker-v2-tile-lock" aria-hidden="true"><Lock size={12} /></span>
                    <small className="locker-v2-tile-hint muted">{hint}</small>
                  </>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function recordLabel(recordId: string): string {
  return RECORDS.find((r) => r.id === recordId)?.label ?? recordId;
}
